import { GroupId } from '@mjolnir/mf-fields-ts/src/groups/GroupId';
import { Payload } from '@mjolnir/mf-fields-ts/src/Payload';
import {
  ArrayOfObjectOfFields,
  EncryptedString,
  Field,
  FieldType,
  ObjectOfFields,
} from '@mjolnir/mf-fields-ts/src/types/Field.type';
import { Group } from '@mjolnir/mf-fields-ts/src/types/Group.type';
import { getValueByPath } from '@mjolnir/mf-fields-ts/src/types/utils';
import { OnboardingField } from '@/bpc/components/OnboardingFieldComponent/OnboardingField';
import { Onboarding } from '@/bpc/components/BaseComponents/Onboarding';
import { PersonRole } from '@mjolnir/mf-fields-ts/src/enm/PersonRole';
import { ContractOnboardingType } from '@mjolnir/mf-fields-ts/src/enm/ContractOnboardingType';

const isArrayOfFields = (o: any): o is ArrayOfObjectOfFields => o.items !== undefined;

const isObjectOfFields = (o: any): o is ObjectOfFields =>
  !isArrayOfFields(o) && o.priority === undefined;

export class OnboardingGroup {
  private readonly onboarding: Onboarding;
  readonly minItems?: number;
  readonly maxItems?: number;
  readonly payload: Payload;
  readonly determineMissingSubgroups?: { (): PersonRole[]; (): any };
  readonly labelTitle;
  readonly fallbackTitle;
  readonly labelSubtitle;
  readonly fallbackSubtitle;
  readonly description;
  readonly mfGroup: Group;
  private applicableCache?: boolean;
  private allMandatoryFilledCache?: boolean;
  private numberOfItems?: number;
  readonly groupId: GroupId;
  // TODO change type to OnboardingField[] or OnboardingFieldsItem[]
  // having: interface OnboardingFieldsItem { fields: OnboardingField[], id: string} while filling id with a UUID and using it as the key prop in OnboardingGroup
  public fields: OnboardingField[][];

  /**
   * Adding new Property for an edge-case to:
   * Force Rendering on specific Onboarding Types when it doesn't have fields
   *  - Should be enabled explicitly on the group for an Onboarding Type
   */
  readonly forceApplicable: boolean;

  constructor(onboarding: Onboarding, group: Group, payload: Payload) {
    this.onboarding = onboarding;
    this.payload = payload;
    this.groupId = group.id;
    this.mfGroup = group;
    this.labelTitle = group.labelTitle;
    this.fallbackTitle = group.fallbackTitle;
    this.labelSubtitle = group.labelSubtitle;
    this.fallbackSubtitle = group.fallbackSubtitle;
    this.description = group.description;
    this.fields = [];

    this.forceApplicable = OnboardingGroup.shouldForceApplicable(onboarding, group);

    const allFields = Object.keys(group.fields);
    if (allFields.length === 1 && isArrayOfFields(group.fields[allFields[0]])) {
      const arrayInformation = (group.fields[allFields[0]] as unknown) as ArrayOfObjectOfFields;
      const itemsInInitialPayload = getValueByPath(
        payload,
        group.fieldPathsOnPayload[allFields[0]],
      );

      let numberOfItemsToRender: number =
        itemsInInitialPayload != null ? itemsInInitialPayload.length : 0;

      if (this.groupId !== GroupId.InStoreStakeholder && this.groupId !== GroupId.KycPersons) {
        numberOfItemsToRender = Math.max(arrayInformation.min, numberOfItemsToRender);
      }

      if (arrayInformation.min !== 1 || arrayInformation.max !== 1) {
        // the following properties cause displaying + / - buttons. We keep them undefined when EXACTLY 1 items MUST be added (no + nor - button)
        this.minItems = arrayInformation.min;
        this.maxItems = arrayInformation.max;
        this.numberOfItems = numberOfItemsToRender;

        // Stakeholder specific
        // @ts-ignore Determine missing role is present on the Person object, and right now it's also ts-ignored there
        this.determineMissingSubgroups = arrayInformation.determineMissingRoles
          ? // @ts-expect-error: FIXME
            () => arrayInformation.determineMissingRoles(this.payload)
          : undefined;
      }
    }

    this.setFields = this.setFields.bind(this);
    this.isApplicable = this.isApplicable.bind(this);
    this.allMandatoryFilled = this.allMandatoryFilled.bind(this);
    this.clearCache = this.clearCache.bind(this);
    this.getValueForField = this.getValueForField.bind(this);
    this.setValueOfField = this.setValueOfField.bind(this);

    this.setFields();
  }

  private setFields(): void {
    this.fields = new Array(this.numberOfItems != null ? this.numberOfItems : 1)
      .fill(undefined)
      .map((_, i) =>
        OnboardingGroup.flattenMfGroupToOnboardingFieldArray(this.mfGroup, this.payload, i),
      );
  }

  public isApplicable(): boolean {
    if (this.applicableCache !== null && this.applicableCache !== undefined) {
      return this.applicableCache;
    }

    const forceApplicable = this.forceApplicable;
    if (forceApplicable) {
      this.applicableCache = forceApplicable;
      return this.applicableCache;
    }

    const hasApplicableFields = this.fields.some(f => f.some(ff => ff.applicable()));
    if (hasApplicableFields) {
      this.applicableCache = hasApplicableFields;
      return this.applicableCache;
    }

    const hasMinimumItems = !!this.minItems && this.minItems > 0;
    /**
     * Not sure what's happening here ??
     * Check if any of the array item fields are applicable
     */
    const hasApplicableArrayItems = OnboardingGroup.flattenMfGroupToOnboardingFieldArray(
      this.mfGroup,
      this.payload,
      0,
    ).some(ff => ff.applicable());

    this.applicableCache = hasMinimumItems && hasApplicableArrayItems;

    return this.applicableCache;
  }

  public allMandatoryFilled(overrideMinItems?: number): boolean {
    const withCache = overrideMinItems == null;

    if (withCache && this.allMandatoryFilledCache != null) {
      return this.allMandatoryFilledCache;
    }

    const checkAllMandatoryFilled = (minItems: number): boolean => {
      // For zero min items we return true since the item list can be left empty
      if (minItems === 0) {
        return true;
      }
      // If the min number of items is not provided, we return false instantly
      if (minItems && minItems > this.fields.length) {
        return false;
      }
      // Otherwise we check each field if it's mandatory and filled
      return this.fields.every(f =>
        f.every(ff => !ff.applicable() || !ff.mandatory() || ff.filled()),
      );
    };

    const allMandatoryFilled = checkAllMandatoryFilled(
      overrideMinItems != null ? overrideMinItems : this.minItems != null ? this.minItems : 1,
    );

    if (withCache) {
      this.allMandatoryFilledCache = allMandatoryFilled;
    }

    return allMandatoryFilled;
  }

  public clearCache(): void {
    this.fields.forEach(f => f.forEach(ff => ff.clearCaches()));
    this.applicableCache = undefined;
    this.allMandatoryFilledCache = undefined;
  }

  public getValueForField(absolutePath: string): boolean | number | string | EncryptedString {
    return getValueByPath(this.payload, absolutePath);
  }

  public setValueOfField(
    field: OnboardingField,
    value: boolean | number | string | EncryptedString | string[],
  ): void {
    this.onboarding.setValueOfField(field, value);
  }

  public canHaveMultipleItems(): boolean {
    return this.numberOfItems != null;
  }

  public canDeleteItem(): boolean {
    return (this.numberOfItems as number) > (this.minItems as number);
  }

  public canAddAdditionalItem(): boolean {
    return (this.numberOfItems as number) < (this.maxItems as number);
  }

  public addItem(): OnboardingField[] {
    this.fields.push(
      OnboardingGroup.flattenMfGroupToOnboardingFieldArray(
        this.mfGroup,
        this.payload,
        this.numberOfItems,
      ),
    );
    (this.numberOfItems as number)++;

    return this.fields[this.fields.length - 1];
  }

  public deleteItem(index: number): void {
    this.fields.splice(index, 1);
    const payloadItems = getValueByPath(
      this.payload,
      this.mfGroup.fieldPathsOnPayload[Object.keys(this.mfGroup.fields)[0]],
    );
    payloadItems?.splice(index, 1);
    (this.numberOfItems as number)--;
    this.setFields();
    this.onboarding.clearCache();
  }

  public nextGroup(): OnboardingGroup {
    return this.onboarding.nextGroup(this.groupId);
  }

  public hasNextGroup(): boolean {
    return this.onboarding.hasNextGroup(this.groupId);
  }

  public previousGroup(): OnboardingGroup {
    return this.onboarding.previousGroup(this.groupId);
  }

  public hasPreviousGroup(): boolean {
    return this.onboarding.hasPreviousGroup(this.groupId);
  }

  public static flattenMfGroupToOnboardingFieldArray = (
    group: Group,
    payload: Payload,
    payloadArrayIndex?: number,
  ): OnboardingField[] =>
    Object.keys(group.fields).flatMap(k =>
      OnboardingGroup.flattenMfFieldToOnboardingFieldArray(
        group.fieldPathsOnPayload[k],
        group.fields[k],
        payload,
        payloadArrayIndex,
      ),
    );

  public static flattenMfFieldToOnboardingFieldArray = (
    path: string,
    field: Field | ObjectOfFields | ArrayOfObjectOfFields,
    payload: Payload,
    payloadArrayIndex?: number,
  ): OnboardingField[] => {
    if (isArrayOfFields(field)) {
      if (
        !isArrayOfFields(field.items) &&
        !isObjectOfFields(field.items) &&
        field.items.type === FieldType.Enum
      ) {
        // special case for ArrayOfEnum
        return [
          new OnboardingField(
            path,
            {
              ...field.items,
              type: FieldType.ArrayOfEnum,
            },
            payload,
            { min: field.min, max: field.max },
          ),
        ];
      }
      return OnboardingGroup.flattenMfFieldToOnboardingFieldArray(
        `${path}[${payloadArrayIndex || 0}]`,
        field.items,
        payload,
        0,
      );
    } else if (isObjectOfFields(field)) {
      return Object.keys(field)
        .sort(
          (a, b) =>
            OnboardingGroup.lowestNestedPriority(field[a]) -
            OnboardingGroup.lowestNestedPriority(field[b]),
        )
        .flatMap(k =>
          OnboardingGroup.flattenMfFieldToOnboardingFieldArray(
            `${path}.${k}`,
            field[k] as Field | ObjectOfFields | ArrayOfObjectOfFields,
            payload,
            0,
          ),
        ); // TODO why contains ObjectOfFields string and function and we need "as"?
    } else {
      return [new OnboardingField(path, field, payload)];
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static lowestNestedPriority = (field: any): number => {
    if (field?.priority != null) {
      return field.priority;
    } else if (typeof field === 'object') {
      return Object.keys(field).reduce(
        (lowestPriorityFound, k) =>
          Math.min(lowestPriorityFound, OnboardingGroup.lowestNestedPriority(field[k])),
        Number.MAX_VALUE,
      );
    } else {
      return Number.MAX_VALUE;
    }
  };

  public static shouldForceApplicable = (onboarding: Onboarding, group: Group): boolean => {
    const DEFAULT_APPLICABLE_FLOWS_FOR_GROUP: Partial<Record<GroupId, ContractOnboardingType[]>> = {
      // [GroupId.ContentStoreGetStarted]: [ContractOnboardingType.UPSELL_WEBSTORE_TO_CONTENT_MANAGER], Flow is deprecated
    };

    // TODO: Update this when we can put it on mf-fields
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const forceApplicableFlows =
      (group as any).forceApplicableFlows || // Expects undefined since property doesn't exist on mf-fields
      DEFAULT_APPLICABLE_FLOWS_FOR_GROUP[group.id] ||
      [];

    if (!forceApplicableFlows || forceApplicableFlows.length === 0) {
      return false;
    }

    return forceApplicableFlows.includes(onboarding.getContractOnboardingType());
  };
}
